import React from "react";
import { FiDownload, FiLoader } from "react-icons/fi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentCompanyId } from "hooks/auth";
import { Modal } from "newui/components/ui/Modal";
import { ConfirmExtractedFields } from "newui/features/Insurance/components/ConfirmExtractedFields";
import { getDocument } from "newui/features/Insurance/api/insuranceVerificationApi";
import { insuranceVerificationKeys } from "newui/features/Insurance/api/insuranceVerificationKeys";
import { companyDocumentKeys } from "../companyDocumentKeys";
import type { CorporateDocument } from "newui/features/Company/types";
import type { InsuranceOcrJobState } from "newui/features/Insurance/types/InsuranceOcrJob.type";

const ACTIVE_STATES: InsuranceOcrJobState[] = ["queued", "running"];
const COMPLETED_STATES: InsuranceOcrJobState[] = ["succeeded", "soft_failed", "failed"];

interface ReviewDraftModalProps {
	companyId: number;
	document: CorporateDocument;
	isOpen: boolean;
	onClose: () => void;
}

export const ReviewDraftModal: React.FC<ReviewDraftModalProps> = ({
	companyId,
	document: initialDocument,
	isOpen,
	onClose,
}) => {
	const currentCompanyId = useCurrentCompanyId() as number;
	const queryClient = useQueryClient();

	const initialState = (initialDocument.latest_ocr_job?.state ?? "queued") as InsuranceOcrJobState;

	// Poll document API while OCR is active to get fresh data when it completes
	const { data: liveDocument } = useQuery({
		queryKey: insuranceVerificationKeys.document(currentCompanyId, initialDocument.id),
		queryFn: () => getDocument(currentCompanyId, initialDocument.id),
		select: (res) => res.data.data,
		enabled: isOpen,
		refetchInterval: (query) => {
			const doc = query.state.data as { latest_ocr_job?: { state?: string }; ocr_job?: { state?: string } } | undefined;
			const state = (doc?.latest_ocr_job?.state ?? doc?.ocr_job?.state) as InsuranceOcrJobState | undefined;
			if (state && !ACTIVE_STATES.includes(state)) {
				return false;
			}
			return ACTIVE_STATES.includes(initialState) ? 5000 : false;
		},
		refetchIntervalInBackground: false,
	});

	// Use live data if available, fall back to initial prop
	const liveState = (liveDocument?.latest_ocr_job?.state ?? liveDocument?.ocr_job?.state ?? initialState) as InsuranceOcrJobState;
	const liveExtraInfo = liveDocument?.extra_info ?? initialDocument.extra_info ?? {};
	const liveOcrJobId = liveDocument?.latest_ocr_job?.id ?? liveDocument?.ocr_job?.id ?? initialDocument.latest_ocr_job?.id ?? null;

	const isActive = ACTIVE_STATES.includes(liveState);
	const isComplete = COMPLETED_STATES.includes(liveState);

	const handleSuccess = () => {
		queryClient.invalidateQueries({
			queryKey: companyDocumentKeys.documents(companyId),
		});
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`Review: ${initialDocument.name}`}
			size="xl"
		>
			<div className="p-4">
				{initialDocument.storage_path && (
					<div className="mb-4 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
						<FiDownload size={16} className="shrink-0 text-blue-600" />
						<span className="text-sm text-gray-700">
							Review the original document alongside the extracted data:
						</span>
						<a
							href={initialDocument.storage_path}
							target="_blank"
							rel="noopener noreferrer"
							className="ml-auto inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-blue-600 ring-1 ring-inset ring-blue-200 transition-colors hover:bg-blue-50"
						>
							<FiDownload size={14} />
							View Document
						</a>
					</div>
				)}

				{isActive && (
					<div className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 p-4 mb-4">
						<FiLoader size={20} className="animate-spin text-blue-600" />
						<div>
							<p className="text-sm font-medium text-blue-800">
								Verifying your document...
							</p>
							<p className="text-xs text-blue-600 mt-0.5">
								You can wait here or close this modal — the status will update automatically.
							</p>
						</div>
					</div>
				)}

				{isComplete && (
					<ConfirmExtractedFields
						documentId={initialDocument.id}
						extraInfo={liveExtraInfo as Record<string, unknown>}
						ocrJobState={liveState}
						ocrJobId={liveOcrJobId}
						onSuccess={handleSuccess}
					/>
				)}
			</div>
		</Modal>
	);
};
